<script lang="ts">
	/**
	 * What is being bought and what it costs, as the sticky rail of the inline checkout (§14).
	 *
	 * Lifted out of the confirmation window when the purchase stopped being a popup. Everything
	 * that made that window worth having is here — it **states, and never re-decides** — and
	 * everything that made it a dialog is not: there is no button, no sign-in, no terms to tick.
	 * The steps beside it collect; this says what they add up to.
	 *
	 * **It will not invent a total.** Tax is computed at the provider under §10, so no figure here
	 * is the final one and the footer says so rather than implying otherwise. The seat-and-coupon
	 * case goes further: a per-unit quote multiplied by a seat count is exact for a percentage and
	 * wrong for a fixed amount — the provider takes a fixed discount once, not once per seat — and
	 * `CouponValidation` deliberately does not say which kind it is (§12's uniform body). So that
	 * one combination reports the discount as applied without a figure, which is the true
	 * statement, rather than a plausible number that is out by a factor of the seat count.
	 *
	 * The change modes render `ProrationPreview` instead of the itemisation, because a change is
	 * not a purchase: what is taken today is a one-off arrived at from credits and part-periods
	 * inside the provider, and any figure computed here would be a second opinion that disagrees
	 * with the invoice.
	 */

	import ProrationPreview from './ProrationPreview.svelte';
	import {
		billingPeriodName,
		formatFriendlyDate,
		formatInterval,
		formatMoney,
		mediaURL
	} from '../Utilities';
	import type {
		CouponValidation,
		Price,
		Product,
		ProductOption,
		ProrationPreview as ProrationPreviewShape,
		StorefrontCurrency
	} from '../AppTypes';

	/** What is being committed to. `buy` itemises; the two change modes quote the server. */
	export let mode: 'buy' | 'change' | 'seats' = 'buy';
	export let product: Product | null = null;
	export let price: Price | null = null;
	/** The whole currency row — every figure here is formatted from its own `minorUnitDigits`. */
	export let currency: StorefrontCurrency | undefined = undefined;
	/** Seats for a per-seat price; 1 everywhere else (§9.7). */
	export let seats: number = 1;
	/** What one of those is called (§4.1) — seats on a subscription, units on a one-time product. */
	export let unitNoun: string = 'user';
	export let unitNounPlural: string = 'users';
	/** The applied coupon and the server's quote for it, or null. */
	export let coupon: { code: string; validation: CouponValidation } | null = null;
	/** The options ticked in the steps beside this, priced. */
	export let selectedOptions: ProductOption[] = [];
	/** The server's quote for a change. Never computed here — proration is the provider's sum. */
	export let preview: ProrationPreviewShape | null = null;
	export let previewLoading: boolean = false;
	export let previewError: string = '';
	/**
	 * Whether a `change` is a move between **billing periods** of one tier rather than between
	 * tiers. The plan is not moving, so nothing here may say it is.
	 */
	export let isBillingChange: boolean = false;
	/** The tier being left, for the scheduled arm's "you keep X until 14 September". */
	export let currentProductName: string = 'your current plan';
	/** Seats held today, so a seat change can say what it is moving from. */
	export let heldSeats: number | null = null;

	$: isSeatBased = price?.isSeatBased === true;
	$: effectiveSeats = isSeatBased ? Math.max(1, seats) : 1;
	/** The noun agreeing with the count actually on screen. */
	$: unitWord = effectiveSeats === 1 ? unitNoun : unitNounPlural;
	/** A seat change is about users by definition; a one-time purchase never reaches that mode. */
	$: unitIcon = unitNoun === 'user' ? 'fa-users' : 'fa-layer-group';
	$: unitAmount = price?.amount ?? null;
	$: subtotal = unitAmount === null ? null : unitAmount * effectiveSeats;

	/**
	 * Whether the coupon's worth can be stated as a figure at all.
	 *
	 * Per unit it always can — that is what the server quoted. Multiplied by a seat count it can
	 * only be trusted for a percentage, and nothing in the uniform validation body distinguishes
	 * the two. Rather than guess, the seat case says the code is applied and lets the payment page
	 * carry the number.
	 */
	$: quoteIsExact = coupon === null || !isSeatBased;
	$: unitFinal = coupon?.validation.finalAmount ?? null;
	$: discount =
		!quoteIsExact || unitAmount === null || unitFinal === null ? null : (unitAmount - unitFinal) * effectiveSeats;

	/**
	 * What the ticked options take off (§4.8), and it is exact in both directions.
	 *
	 * Percentages add and are capped at the whole price; fixed amounts add and are capped at the
	 * subtotal, because a discount larger than the price is a free purchase rather than a negative
	 * one — the same arithmetic the server does, so the figure here is the figure charged. A fixed
	 * amount is **not** multiplied by the seat count: the provider takes it once, which is the
	 * trap the coupon quote above refuses to walk into.
	 *
	 * A coupon and a discounting option can never both be set — the steps disable each while the
	 * other is chosen and the server refuses the pair — so these two lines cannot both appear.
	 */
	/**
	 * Which side of the invoice each ticked option lands on, from the **sign** of its own value.
	 *
	 * Negative takes money off and positive puts it on, and the two are not two ways of writing one
	 * number: a discount composes into the provider's single coupon and a charge becomes a line
	 * item of its own, because a coupon can only ever reduce a price. They are summed separately
	 * here for the same reason, and both can be present on one purchase.
	 */
	$: optionPercentOff = Math.min(
		100,
		selectedOptions
			.filter(option => option.adjustmentType === 'percent' && (option.percentAdjustment ?? 0) < 0)
			.reduce((total, option) => total + Math.abs(option.percentAdjustment ?? 0), 0)
	);
	$: optionFixedOff = selectedOptions
		.filter(option => option.adjustmentType === 'fixed' && (option.amount ?? 0) < 0)
		.reduce((total, option) => total + Math.abs(option.amount ?? 0), 0);
	$: optionDiscount =
		subtotal === null
			? null
			: optionPercentOff > 0
				? Math.round((subtotal * optionPercentOff) / 100)
				: optionFixedOff > 0
					? Math.min(optionFixedOff, subtotal)
					: null;

	/**
	 * The discounts, one line each, exactly as the extras are.
	 *
	 * A combined "Options −€2.85" is the same mistake a combined extras line would be: a customer
	 * who ticked two things is owed both by name, and it is the only place they can check that the
	 * one they cared about was actually counted. Ticking a third and watching one figure move says
	 * nothing about which of the three did it.
	 *
	 * The **total is the authority**, not the sum of these rows. Percentages compose before they
	 * are applied and the whole is capped at the subtotal, so a per-line figure computed
	 * independently can round or overshoot away from what is actually taken off. So the composed
	 * figure is allocated across the rows in order — each takes at most what is left, and the last
	 * absorbs the remainder — and the lines always add up to the discount above them.
	 */
	$: optionDiscounts = allocate(
		selectedOptions
			.filter(option =>
				option.adjustmentType === 'percent'
					? (option.percentAdjustment ?? 0) < 0
					: option.adjustmentType === 'fixed' && (option.amount ?? 0) < 0
			)
			.map(option => ({
				uuid: option.uuid,
				title: option.title,
				percentOff:
					option.adjustmentType === 'percent' ? Math.abs(option.percentAdjustment ?? 0) : null,
				raw:
					subtotal === null
						? 0
						: option.adjustmentType === 'percent'
							? Math.round((subtotal * Math.abs(option.percentAdjustment ?? 0)) / 100)
							: Math.abs(option.amount ?? 0)
			})),
		optionDiscount ?? 0
	);

	/** Shares `total` across `rows` in order, so the rows always sum to it exactly. */
	function allocate<T extends { raw: number }>(rows: T[], total: number): (T & { amount: number })[] {
		let remaining = total;
		return rows.map((row, index) => {
			const amount = index === rows.length - 1 ? remaining : Math.min(row.raw, remaining);
			remaining -= amount;
			return { ...row, amount };
		});
	}

	/**
	 * The extras, one line each, because that is what the invoice will show.
	 *
	 * A percentage charge is resolved against the subtotal here exactly as the server resolves it,
	 * so the figure on this screen is the figure on the line item. **Not capped at the subtotal**,
	 * unlike a discount: taking more off than the price is a free purchase, while adding more than
	 * the price is an ordinary sale — a €250 onboarding fee on a €25 plan.
	 */
	$: optionCharges = selectedOptions
		.filter(option =>
			option.adjustmentType === 'percent'
				? (option.percentAdjustment ?? 0) > 0
				: option.adjustmentType === 'fixed' && (option.amount ?? 0) > 0
		)
		.map(option => ({
			uuid: option.uuid,
			title: option.title,
			once: option.chargeOnce === true,
			amount:
				subtotal === null
					? 0
					: option.adjustmentType === 'percent'
						? Math.round((subtotal * (option.percentAdjustment ?? 0)) / 100)
						: option.amount ?? 0
		}));
	$: optionChargeTotal = optionCharges.reduce((total, charge) => total + charge.amount, 0);

	$: dueBeforeTax =
		subtotal === null
			? null
			: subtotal - (discount ?? 0) - (optionDiscount ?? 0) + optionChargeTotal;

	/**
	 * A trial charges nothing today, and the whole point of saying so here is that the figure the
	 * customer is about to see on the provider's page is zero. The renewal date is derived rather
	 * than quoted because the trial has not started yet — there is no server-side date to ask for.
	 */
	$: trialDays = price?.trialDays ?? 0;
	$: hasTrial = trialDays > 0;
	$: firstChargeDate = hasTrial ? new Date(Date.now() + trialDays * 86_400_000) : null;

	$: intervalSuffix = price === null ? '' : formatInterval(price.interval, price.intervalCount);
	$: isSubscription = product?.type === 'subscription';
	$: isChange = mode !== 'buy';

	/**
	 * How often the target price bills, in words: "yearly billing", "billing every 3 months".
	 *
	 * It is what a **billing change** is moving to. `ProrationPreview`'s scheduled arm reads "you
	 * keep X until 14 September, then move to Y", and on a billing change X and Y are the same
	 * tier — so naming the product in both halves produces "then move to Polyverse Pro" for a
	 * customer already on Polyverse Pro. The period is the thing that is actually changing.
	 */
	$: targetBillingPeriod = price === null ? '' : billingPeriodName(price);

	/** Formats one of the figures above, or an em dash while the currency row is still loading. */
	function money(amountMinor: number | null): string {
		if (amountMinor === null || currency === undefined) return '—';
		return formatMoney(amountMinor, currency);
	}
</script>

{#if product !== null && price !== null}
	<div class="widget bg-depth space-y-4">
		<p class="card-label"><i class="fa-solid fa-receipt text-[11px]" />Your order</p>

		<!-- What, before how much: the product is the thing being agreed to. -->
		<div class="flex items-start gap-3">
			{#if product.imageURL}
				<img src={mediaURL(product.imageURL)} alt="" class="w-11 h-11 rounded-xl object-contain shrink-0" />
			{/if}
			<div class="min-w-0">
				<!--
					The name, and what kind of purchase it is. **Not the tagline**: the page's own
					header is still on screen a few pixels away, and a summary that restates it
					spends the rail's width saying something the customer has already read.
				-->
				<div class="font-semibold leading-tight">{product.name}</div>
				<div class="text-xs text-default mt-1">
					{#if mode === 'seats'}
						<i class="fa-solid fa-users mr-1 text-[11px]" />
						{#if heldSeats !== null}
							{heldSeats} → {effectiveSeats} {unitWord}
						{:else}
							{effectiveSeats} {unitWord}
						{/if}
					{:else if mode === 'change' && isBillingChange}
						<!-- The plan is not moving. Saying it is would describe a change the customer
						     did not ask for, on the screen where they check what they are agreeing to. -->
						<i class="fa-solid fa-calendar-days mr-1 text-[11px]" />Staying on {currentProductName},
						billed{intervalSuffix}
					{:else if mode === 'change'}
						<!-- Which way this goes, in words: "moving from Pro" is the fact the customer
						     is checking, and it is the one the tier name alone does not carry. -->
						<i class="fa-solid fa-arrow-right-arrow-left mr-1 text-[11px]" />Moving from
						{currentProductName}
					{:else if isSubscription}
						<i class="fa-solid fa-repeat mr-1 text-[11px]" />Recurring{intervalSuffix}, cancel any time
					{:else}
						<i class="fa-solid fa-box mr-1 text-[11px]" />One-time purchase
					{/if}
				</div>
			</div>
		</div>

		{#if isChange}
			<!--
				The server's quote, not ours. Proration is arrived at from credits and part-periods
				inside the provider, and any figure computed here would be a second opinion that
				disagrees with the invoice.
			-->
			<ProrationPreview
				{preview}
				loading={previewLoading}
				error={previewError}
				{currentProductName}
				targetProductName={isBillingChange ? targetBillingPeriod : product.name}
			/>
		{:else}
			<!--
				The itemisation. Every row is either the server's own quote or arithmetic on it, and
				the one row that cannot be either says so instead of showing a number.
			-->
			<dl class="text-sm space-y-2 border-t border-widget-color pt-4">
				{#if isSeatBased}
					<div class="flex items-baseline justify-between gap-4">
						<dt class="text-default">
							<i class={`fa-solid ${unitIcon} mr-1.5 text-[11px]`} />{effectiveSeats}
							{unitWord} × {money(unitAmount)}
						</dt>
						<dd class="tabular-nums">{money(subtotal)}</dd>
					</div>
				{:else}
					<div class="flex items-baseline justify-between gap-4">
						<dt class="text-default">Subtotal</dt>
						<dd class="tabular-nums">{money(subtotal)}</dd>
					</div>
				{/if}

				{#if coupon !== null}
					<div class="flex items-baseline justify-between gap-4 text-green-700 dark:text-green-400">
						<dt>
							<i class="fa-solid fa-tag mr-1.5 text-[11px]" />Discount
							<span class="font-mono text-xs">{coupon.code}</span>
						</dt>
						<dd class="tabular-nums">
							{#if discount !== null}
								−{money(discount)}
							{:else}
								<!-- Applied, but not quotable per seat from a body that hides its kind. -->
								<span class="text-xs">applied at checkout</span>
							{/if}
						</dd>
					</div>
				{/if}

				{#each optionCharges as charge (charge.uuid)}
					<!--
						One row per extra, not a combined figure: a plan, priority support and an
						onboarding fee are three things the customer agreed to separately and will
						see as three lines on the invoice.
					-->
					<div class="flex items-baseline justify-between gap-4">
						<dt class="text-default">
							<i class="fa-solid fa-plus mr-1.5 text-[11px]" />{charge.title}
							{#if charge.once}
								<span class="text-xs">(one-off)</span>
							{/if}
						</dt>
						<dd class="tabular-nums">{money(charge.amount)}</dd>
					</div>
				{/each}

				{#each optionDiscounts as saving (saving.uuid)}
					<!-- One row per option, for the reason the extras get one each. -->
					<div class="flex items-baseline justify-between gap-4 text-green-700 dark:text-green-400">
						<dt>
							<i class="fa-solid fa-minus mr-1.5 text-[11px]" />{saving.title}
							{#if saving.percentOff !== null}
								<span class="text-xs">({saving.percentOff}% off)</span>
							{/if}
						</dt>
						<dd class="tabular-nums">−{money(saving.amount)}</dd>
					</div>
				{/each}

				<div class="border-t border-widget-color pt-3 flex items-baseline justify-between gap-4">
					<dt class="font-semibold">Due today</dt>
					<dd class="text-2xl font-bold tabular-nums">
						{#if hasTrial}
							{money(0)}
						{:else}
							{money(dueBeforeTax)}
						{/if}
					</dd>
				</div>
			</dl>

			{#if hasTrial && firstChargeDate !== null}
				<p class="text-sm">
					<i class="fa-solid fa-gift text-primary-500 mr-1.5" />Free for {trialDays} days. After that
					{money(dueBeforeTax)}{intervalSuffix}, starting {formatFriendlyDate(firstChargeDate)}.
				</p>
			{/if}

			<!--
				§10 computes tax at the provider, so no figure above is the final one. Saying so is
				not a disclaimer — it is the difference between a summary and a quote. A change has
				its tax quoted in the proration above, so this is a purchase's sentence only.
			-->
			<p class="text-xs text-default">
				{#if price.taxBehavior === 'inclusive'}
					Tax is included in these figures.
				{:else}
					Tax is added where applicable and is calculated on the payment page.
				{/if}
				{#if !quoteIsExact}
					Your discount is applied there too.
				{/if}
			</p>
		{/if}

	</div>
{/if}
